import { Button, Dialog, DialogTitle, TextField, IconButton, Tooltip, Typography } from '@mui/material';
import { DropDownButton } from '@progress/kendo-react-buttons';
import { useSaveData, useSaveDataForFastField, useSaveDataForFastFieldRecall } from '../../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../../api/workorders-api-key-store';
import { WorkOrder } from '../../../models/workOrder';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ReadyDialogComponent } from './ready-dialog.component';
import dayjs from 'dayjs';
import { WorkFlowDialogComponenet } from './work-flow-dialog-componenet';
import { CurtainsOutlined } from '@mui/icons-material';
import { SelectServiceDialog } from '../edit-workorder/select-service--dialog';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface WorkOrderStatusButtonsProps {
  workOrder?: WorkOrder;
  reloadData?: any;
}

const FILTERED_WORKORDER_STATUS = ['Complete', 'Pass', 'Fail', 'Partial Pass'];
const WORKFLOW_STATUS = ['Fail', 'Partial Pass', 'Cancel', 'Pass', 'Waived', 'Complete', 'Not Ready']; //complete used only in proctor,Not Ready used in ppi only

const FAST_FIELD_FORMID_ONE = [978588, 981259, 981474, 981561, 981862, 982092, 982408, 982791, 951425, 983430]; //3rd party only re-inspection forms
const FAST_FIELD_FORMID_TWO = [978588, 981259, 981474, 981862, 982092, 982408, 982791, 983430, 1034181]; //3rd party and ppi re-inspection forms

export const WorkOrderStatusButtons = ({ workOrder, reloadData }: WorkOrderStatusButtonsProps) => {
  const { isLoading, mutateAsync } = useSaveData(workOrdersApiKeyStore.updateWorkOrderStatus());
  // const { isLoading: isLoadingFastfieldRecall, mutateAsync: fastfieldRecall } = useSaveDataForFastFieldRecall(
  //   workOrdersApiKeyStore.fastFieldRecall(workOrder?.workOrderID)
  // );
  const { isLoading: isLoadingFastfieldDispatch, mutateAsync: fastfieldDispatch } = useSaveDataForFastField(
    workOrdersApiKeyStore.fastFieldDispatch(workOrder?.workOrderID, 'single')
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenReadyDialog, setOpenReadyDialog] = useState(false);
  const [isOpenServiceDialog, setOpenServiceDialog] = useState(false);
  const [selectedServiceID, setSelectedServiceID] = useState<number>(0);
  const [selectedFastFieldFormID, setSelectedFastFieldFormID] = useState<number>(0);
  const [isOpenReason, setIsOpenReason] = useState(false);
  const [isOpenWorkFlowDialog, setIsWorkFlowDialog] = useState({
    isDialogOpen: false,
    isDropDown: false,
    isProctor: false,
  });
  const [status, setStatus] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [inspectionResult, setInspectionResult] = useState<string>('');
  const [scheduledDate, setScheduledDate] = useState<any>();
  const [workFlowField, setWorkFlowField] = useState<any>({
    isReinspectionRequired: undefined,
    inspectionCodeResult: undefined,
  });
  const [reasonError, setReasonError] = useState<boolean>(false);

  const resetWorkFlowField = () => {
    setWorkFlowField({
      isReinspectionRequired: undefined,
      inspectionCodeResult: undefined,
    });
    setInspectionResult('');
  };

  const handleStatusClick = async (scheduleDate: any, status: string) => {
    const formatDate = (date: any) => {
      if (!date) return ''; // Handle null or undefined dates
      return dayjs(date).format('YYYY-MM-DD'); // Format as YYYY-MM-DD
    };
    const data = {
      workOrderID: workOrder?.workOrderID,
      status: status,
      resultChangeReason: reason,
      resultChange: !!reason,
      proposedScheduledDate: formatDate(scheduleDate),
      isReinspectionRequired: !workFlowField?.isReinspectionRequired ? false : workFlowField?.isReinspectionRequired,
      inspectionCodeResult: !workFlowField?.inspectionCodeResult ? '' : workFlowField?.inspectionCodeResult,
      inspectionResult: !inspectionResult ? '' : inspectionResult,
      serviceID: selectedServiceID,
    };

    await mutateAsync({ data: data });
    reloadData();
    setIsOpen(false);
    setScheduledDate('');
    resetWorkFlowField();
    setIsOpenReason(false);
    setOpenReadyDialog(false);
    setOpenServiceDialog(false);
    setSelectedServiceID(0);
    setSelectedFastFieldFormID(0);
    setReason('');
    setStatus('');
    setIsWorkFlowDialog({
      isDialogOpen: false,
      isDropDown: false,
      isProctor: false,
    });
    handleWorkFlowDialogClosed();
    if (status === 'Cancel' || status === 'UnSchedule') {
      // fastfieldDispatch({ data: {} });
    }
  };

  const openDialog = (status: string, showNewResultPopup: boolean) => {
    setStatus(status);
    setSelectedFastFieldFormID(0);

    if (workOrder?.showDryrunCompleteValidationMsg) {
      handleStatusClick(scheduledDate, status);
      return;
    }

    if (status === 'Ready') {
      setOpenReadyDialog(true);
      return;
    }

    if (workOrder?.serviceID === 0 && workOrder?.workOrderStatus === 'Scheduled') {
      // if (status === 'UnSchedule') {
      ///PPI only Retest wo with no serviceid
      setOpenServiceDialog(true);
      setSelectedServiceID(0);
      return;
      // } else {
      //   //set original service id as workorder service
      //   setSelectedServiceID(workOrder?.originalServiceID);
      //   setSelectedFastFieldFormID(workOrder?.originalFastFieldFormID);
      // }
    }
    if (!showNewResultPopup && workOrder?.workOrderStatus === 'Scheduled') {
      handleStatusDependentDialog(status);
      //setIsOpen(true);
      return;
    }
    if (workOrder?.workOrderStatus && FILTERED_WORKORDER_STATUS.includes(workOrder?.workOrderStatus)) {
      //cancel, new result change (pass,fail,complete,partial pass)
      setIsOpenReason(true);

      return;
    } // dry run, not ready, Late Cancellation
    else {
      setIsOpen(true);

      return;
    }
  };

  const handleStatusDependentDialog = (status: string) => {
    if (WORKFLOW_STATUS.includes(status)) {
      handleWorkFlowStatusClick(status);
    } else {
      setIsOpen(true);
    }
  };

  const handleWorkFlowStatusClick = (status: string) => {
    const { fastFieldFormID, ffServiceCat } = workOrder || {};
    const failOrPass = status === 'Fail' || status === 'Pass';

    const handleFailPassDialog = (isDropDown: boolean) => {
      setIsWorkFlowDialog({ isDialogOpen: true, isDropDown, isProctor: false });
      resetWorkFlowField();
      setInspectionResult(status);
    };

    const setInspectionResultDirectly = () => {
      setInspectionResult(status);
      setIsOpen(true);
    };
    const newFastFieldFormID = !selectedFastFieldFormID ? fastFieldFormID : selectedFastFieldFormID;

    /* Energy Inspections Start*/

    if (
      newFastFieldFormID === 1025679 || //newFastFieldFormID === 949254 || FFFormName ='ESTAR Thermal Bypass'
      newFastFieldFormID === 1026115 || //newFastFieldFormID === 968502 ||FFFormName ='Energy Star Thermal Bypass Reinspection'
      newFastFieldFormID === 949012 || //'HERS Thermal Bypass'
      //newFastFieldFormID === 951500 ||  --@FFFormName ='FGBC 1'  no need to set value to @InspectResults
      newFastFieldFormID === 972665 || //@FFFormName ='Indoor Air Plus 1'
      newFastFieldFormID === 1030709 ||
      newFastFieldFormID === 1036678 || //newFastFieldFormID === 986741 ||FFFormName ='ESTAR Final Inspection3_11'
      newFastFieldFormID === 1036676 || //newFastFieldFormID === 986743 ||FFFormName ='ESTAR Final Reinspection 3_11'
      newFastFieldFormID === 951690 || //FFFormName ='HERS Final'
      newFastFieldFormID === 990611 || //FFFormName ='HERS Final Retest'
      //newFastFieldFormID === 951508 || //FFFormName ='FGBC 2'  no need to set value to @InspectResults
      newFastFieldFormID === 972679 || //FFFormName ='Indoor Air Plus 2'
      newFastFieldFormID === 1030831 ||
      newFastFieldFormID === 952688 || //FFFormName ='Code Blower Door'
      newFastFieldFormID === 1033162 ||
      newFastFieldFormID === 952650 || //FFFormName ='Code Blower Door/Duct Blaster'
      newFastFieldFormID === 991490 || //FFFormName ='Code Blower Door & Duct Leakage Retest'
      //newFastFieldFormID === 979918 || //FFFormName ='Quality Check'  no need to set value to @InspectResults
      newFastFieldFormID === 980817 || //FFFormName ='Quality Check Follow Up (QCFollow Up) Tampa'
      newFastFieldFormID === 991616 || //FFFormName ='Quality Check Follow Up(QC Follow Up) Tampa Reinspection'
      newFastFieldFormID === 951471 || //FFFormName ='Florida Water Star'
      newFastFieldFormID === 1031025
    ) {
      if (status === 'Fail' || status === 'Pass') {
        setInspectionResultDirectly(); //@InspectResults=fail/pass
      } else {
        setIsOpen(true);
      }
    } else if (newFastFieldFormID === 951508 || newFastFieldFormID === 951500 || newFastFieldFormID === 979918) {
      setIsOpen(true); //status is not ready means IsPropertyReady = 0
    } else if (newFastFieldFormID === 953629 || newFastFieldFormID === 1033315) {
      /* Energy Inspections End*/

      /*Proctor Test Start*/
      //set @InspectResults ='proctor sample collection not completed'/'proctor sample collection completed'
      if (status === 'Fail') {
        setInspectionResult('proctor sample collection not completed');
      } else if (status === 'Complete' || status === 'Pass') {
        setInspectionResult('proctor sample collection completed');
      }
      setIsOpen(true);
    } else if (newFastFieldFormID === 952697 || newFastFieldFormID === 1033729) {
      /*Proctor Test End*/

      /*Compaction Test Start*/
      if (status === 'Fail' || status === 'Pass') {
        //set @InspectResults=pass/fail
        setInspectionResultDirectly();
      } else {
        setIsOpen(true);
      }
    } else if (newFastFieldFormID === 1048130) {
      /*Compaction Test End*/

      /*Roof and Wall Starts*/
      if (status === 'Complete') {
        //set @InspectResults=Complete
        setInspectionResultDirectly();
      } else {
        setIsOpen(true);
      }
    } else if (newFastFieldFormID === 980342) {
      /*Roof and Wall end*/

      /* Engineering Inspections Start*/
      switch (ffServiceCat) {
        case 'PPI Only':
          //if (status === 'Complete' || status === 'Pass' || status === 'Not Ready') {
          handleFailPassDialog(true);
          // } else {
          //   setInspectionResultDirectly(); //set @InspectResults=pass/waived
          // }
          // if (status === 'Fail' || status === 'Partial Pass' || status === 'Cancel') {
          //   setWorkFlowField({
          //     ...workFlowField,
          //     inspectionCodeResult: status, //set @InspectionCodeResult=fail/partial pass/cancelled
          //   });
          //   setIsOpen(true);
          // } else if (status === 'Pass' || status === 'Waived') {
          //   setInspectionResultDirectly(); //set @InspectResults=pass/waived
          // } else {
          //   setIsOpen(true);
          // }
          break;
        case 'Third-Party Only':
          if (status === 'Fail') {
            handleFailPassDialog(false); //set @InspectionCodeResult,@InspectResults=fail,@IsReinspectionRequired
          } else if (status === 'Pass') {
            setInspectionResultDirectly(); //set @InspectResults=pass
          } else {
            setIsOpen(true);
          }
          break;
        default: //'Third-Party and PPI'
          if (ffServiceCat === 'Third-Party and PPI') {
            if (failOrPass) {
              handleFailPassDialog(true); //set @InspectResults,@InspectionCodeResult
            } else {
              setIsOpen(true);
            }
          } else {
            setIsOpen(true);
          }
          break;
      }
    } else if (newFastFieldFormID === 986754 && ffServiceCat === 'PPI Only') {
      //if (status === 'Complete' || status === 'Pass' || status === 'Not Ready') {
      handleFailPassDialog(true);
      // } else {
      //   setInspectionResultDirectly(); //set @InspectResults=pass/waived
      // }

      // if (
      //   status === 'Fail' ||
      //   status === 'Partial Pass' ||
      //   status === 'Cancel' ||
      //   status === 'Pass' ||
      //   status === 'Waived'
      // ) {
      //   //for fail/Partial Pass/cancelled/Pass/Waived set @InspectionCodeResult
      //   setWorkFlowField({
      //     ...workFlowField,
      //     inspectionCodeResult: status, //set @InspectionCodeResult=Pass/Waived
      //   });
      //   setIsOpen(true);
      // }
    } else if (
      ffServiceCat === 'Third-Party Only' &&
      (FAST_FIELD_FORMID_ONE.includes(newFastFieldFormID || 0) || [981777, 981833].includes(newFastFieldFormID || 0))
    ) {
      status === 'Fail' ? handleFailPassDialog(false) : setInspectionResultDirectly();
    } else if (
      ffServiceCat === 'Third-Party and PPI' &&
      FAST_FIELD_FORMID_TWO.includes(newFastFieldFormID || 0) &&
      failOrPass
    ) {
      handleFailPassDialog(true); //set @InspectResults=pass/fail,@InspectionCodeResult=fail/,partial pass/cancelled/waived
    } else {
      setIsOpen(true);
    }
    /* Engineering Inspections End*/
  };

  const closeDialog = () => {
    setIsOpen(false);
    if (status !== 'Ready') {
      setStatus('');
      setReason('');
    }
  };

  const closeDialogReason = () => {
    setIsOpenReason(false);
    setStatus('');
    setReason('');
  };

  const submitHanlder = () => {
    handleStatusClick(scheduledDate, status);
  };

  const handleSaveClick = () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    handleStatusDependentDialog(status);
    // setIsOpen(true);
  };

  const handleScheduleDate = (isPrerequest: boolean, date: any) => {
    if (isPrerequest) handleStatusClick(date, status);
    else {
      setScheduledDate(date);
      setIsOpen(true);
    }
  };

  const handleWorkFlowFormSubmit = (data: any) => {
    setWorkFlowField(data);
    const newFastFieldFormID = !selectedFastFieldFormID ? workOrder?.fastFieldFormID : selectedFastFieldFormID;
    if (
      workOrder?.ffServiceCat === 'Third-Party and PPI' &&
      FAST_FIELD_FORMID_TWO.includes(newFastFieldFormID || 0) &&
      status === 'Fail' && //|| status === 'Pass'
      (data?.inspectionCodeResult === 'Pass' || data?.inspectionCodeResult === 'Waived') &&
      !data?.isReinspectionRequired
    ) {
      setIsWorkFlowDialog({ isDialogOpen: true, isDropDown: false, isProctor: false });
      setWorkFlowField({
        isReinspectionRequired: undefined,
        inspectionCodeResult: undefined,
      });
    }
    //   else if(workOrder?.ffServiceCat === 'PPI Only'
    //     && (status==='Fail'||status==='Partial Pass'|| status==='Cancel' || status==='Pass' || status==='Waived') &&
    //     newFastFieldFormID===986754
    // )
    //   {
    //     setIsWorkFlowDialog({ isDialogOpen: false, isDropDown: false, isProctor: false });
    //     setWorkFlowField({
    //       isReinspectionRequired:data?.isReinspectionRequired,
    //       inspectionCodeResult: status,
    //     });
    //     setIsOpen(true);
    //   }
    else {
      // if (isOpenWorkFlowDialog?.isProctor) {
      //   setInspectionResult(data?.inspectionCodeResult);
      //   setIsWorkFlowDialog({ isDialogOpen: false, isDropDown: false, isProctor: false });
      //   setWorkFlowField({
      //     isReinspectionRequired: undefined,
      //     inspectionCodeResult: undefined,
      //   });
      // }
      setIsOpen(true);
    }
  };

  const handleReadyDialogClosed = () => {
    setOpenReadyDialog(false);
    setStatus('');
    setScheduledDate('');
    setWorkFlowField({
      isReinspectionRequired: undefined,
      inspectionCodeResult: undefined,
    });
  };

  const handleWorkFlowDialogClosed = () => {
    setIsWorkFlowDialog({ ...isOpenWorkFlowDialog, isDialogOpen: false, isProctor: false });
    setStatus('');
  };

  const hanldeSelectServiceId = (value: number, fastFieldFormIDValue: number) => {
    setSelectedServiceID(value);
    setSelectedFastFieldFormID(fastFieldFormIDValue);
  };

  const handleServiceSave = () => {
    handleStatusDependentDialog(status);
    // setIsOpen(true);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      {workOrder?.workOrderStatuses
        //?.filter((c) => c.isEnabled)
        .map((s) => (
          <Button //disabled={workOrder?.workOrderStatus === 'Scheduled' && !workOrder.ffDispatchID}
            className="btn btn-primary btn-sm "
            //className={workOrder?.workOrderStatus === 'Scheduled' && !workOrder.ffDispatchID?`btn btn-sm`:`btn btn-primary btn-sm`}

            style={{ paddingBottom: '4px', paddingTop: '5px', marginRight: '5px' }}
            key={s.workOrderStatus}
            onClick={() => openDialog(s.workOrderStatus, s.showNewResultPopup)}
          >
            {s.workOrderStatus}
          </Button>
        ))}
      {/* {workOrder?.workOrderStatuses
        ?.filter((c) => !c.isEnabled)
        .map((s) => (
          <Button
            disabled
            variant="outlined"
            style={{ paddingBottom: '4px', paddingTop: '5px', marginRight: '5px' }}
            key={s.workOrderStatus}
          >
            {s.workOrderStatus}
          </Button>
        ))} */}

      <LoadingIndicator isLoading={isLoading} />
      {isOpen && (
        <Dialog
          open={isOpen}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                {status === 'Cancel'
                  ? 'Are you sure that you want to cancel this work order?'
                  : // :status === 'Unschedule' && workOrder?.workOrderStatus==='Scheduled' && workOrder.ffDispatchID!==""
                  // ? 'This work order is waiting for Fastfield results. Please cross-check the work order status from the Fastfield before unschedule. Do you want to continue?'
                  'Are you sure you want to change the status?'}
              </div>
              <LoadingIndicator isLoading={isLoading} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeDialog}
                autoFocus
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer'
                }}
                type="button"
                onClick={submitHanlder}
                disabled={isLoading || isReadOnlyPermission}
              >
                Confirm
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
      {isOpenReason && (
        <Dialog
          open={isOpenReason}
          className="modalpopup"
          title={'REASON'}
          // onClose={closeDialogReason}
          maxWidth={'md'}
          fullWidth={true}
        >
          <DialogTitle id="alert-dialog-title" sx={{ p: 2, pt: 1, pb: 1, m: 0 }}>
            Reason
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialogReason}
            sx={{
              position: 'absolute',
              right: 10,
              top: 3,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Tooltip title={<Typography variant="body1">Close</Typography>}>
              <CloseIcon />
            </Tooltip>
          </IconButton>
          <div style={{ padding: '15px 30px' }}>
            <TextField
              id="reason"
              label="Reason for result change"
              multiline
              rows={4}
              variant="outlined"
              value={reason}
              onChange={(e: any) => {
                setReason(e.target.value);
                setReasonError(false);
              }}
              error={reasonError}
              helperText={reasonError ? 'Reason is required' : ''}
              fullWidth
              margin="normal"
            />
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeDialogReason}
                autoFocus
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer'
                }}
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || isReadOnlyPermission}
              >
                Save
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {isOpenReadyDialog && (
        <ReadyDialogComponent
          workorderID={workOrder?.workOrderID}
          onScheduleDate={handleScheduleDate}
          onClosed={handleReadyDialogClosed}
          // inspectionRequestOn={workOrder?.inspectionRequestOn}
          isParentLoading={isLoading}
          inspectionRequestOn={
            workOrder?.inspectionRequestType === 'InspectionRequest' && workOrder?.workOrderStatus === 'Forecast'
              ? workOrder?.proposedScheduledDate
              : ''
          }
        />
      )}
      {isOpenWorkFlowDialog?.isDialogOpen && (
        <WorkFlowDialogComponenet
          handleWorkFlowFormSubmit={handleWorkFlowFormSubmit}
          onClosed={handleWorkFlowDialogClosed}
          isOpenWorkFlowDialog={isOpenWorkFlowDialog}
        />
      )}
      {isOpenServiceDialog && (
        <SelectServiceDialog
          open={isOpenServiceDialog}
          unitID={workOrder?.unitID}
          onClose={() => setOpenServiceDialog(false)}
          onHandleSetSelectedServiceId={hanldeSelectServiceId}
          selectedServiceID={selectedServiceID}
          handleServiceSave={handleServiceSave}
          workOrder={workOrder}
        />
      )}
    </>
  );
};
